import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Container,
  Typography,
  Button,
  Grid,
  Box,
  CircularProgress,
  IconButton,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Add, Assessment, ChevronLeft, ChevronRight } from '@material-ui/icons';
import { BrandCard } from '../components/BrandCard';
import { getDemoReports } from 'api_handlers/competitor';

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    minHeight: '100vh',
    backgroundColor: '#ffffff',
    textAlign: 'left',
    padding: '10px',
  },
  content: {
    height: '100vh',
    overflow: 'scroll',
    flexGrow: 1,
    padding: theme.spacing(4),
    boxSizing: 'border-box',
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },
  contentShift: {
    height: '100vh',
    overflow: 'scroll',
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    boxSizing: 'border-box',
  },
  pageContent: {
    padding: '0px',
  },
  hero: {
    textAlign: 'center',
    padding: theme.spacing(6, 0),
  },
  iconContainer: {
    height: 50,
    width: 50,
    borderRadius: theme.shape.borderRadius,
    background: 'linear-gradient(135deg, #f9a620 0%, #548c2f 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto',
    marginBottom: theme.spacing(2),
  },
  icon: {
    color: '#fff',
    fontSize: 32,
  },
  title: {
    fontSize: '32px',
    fontWeight: 700,
    marginBottom: '10px',
  },
  subtitle: {
    fontSize: '16px',
    color: theme.palette.text.secondary,
    maxWidth: 600,
    margin: '0 auto',
    marginBottom: '0px',
  },
  section: {
    marginTop: '20px',
  },
  sectionTitle: {
    fontSize: '1.5rem',
    fontWeight: 700,
    marginBottom: theme.spacing(1),
  },
  sectionDescription: {
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(4),
  },
  paginationContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(2),
    marginTop: theme.spacing(3),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  paginationInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
}));

export default function Landing({ onViewReport, onExportReport }) {
  const classes = useStyles();
  const history = useHistory();
  const [brandGroups, setBrandGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Check if component is loaded in an iframe
  const isInIframe = window.self !== window.top;
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    count: 0,
    next: null,
    previous: null,
  });
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    loadDemoReports(1);
  }, []);

  // Sync URL with parent window when in iframe
  useEffect(() => {
    if (isInIframe) {
      const syncUrlWithParent = () => {
        const currentPath = history.location.pathname;
        const search = history.location.search;
        window.parent.postMessage({
          type: 'COMPETITOR_REPORT_NAVIGATE',
          path: `/planner${currentPath}${search}`
        }, '*');
      };

      // Sync on mount
      syncUrlWithParent();

      // Listen for history changes
      const unlisten = history.listen((location) => {
        syncUrlWithParent();
      });

      // Listen for browser back/forward buttons
      window.addEventListener('popstate', syncUrlWithParent);

      return () => {
        unlisten();
        window.removeEventListener('popstate', syncUrlWithParent);
      };
    }
  }, [isInIframe, history]);


  const loadDemoReports = async (page = 1) => {
    try {
      setLoading(true);
      setError(null);
      const response = await getDemoReports(page);

      let brandGroupsList = [];
      let totalCount = 0;

      if (response) {
        // Case 1: Response has results array (pagination structure)
        if (response.results && Array.isArray(response.results)) {
          brandGroupsList = response.results.filter(
            (bg) => bg.reports && Array.isArray(bg.reports) && bg.reports.length > 0,
          );
          totalCount = response.count || brandGroupsList.length;
          setPagination({
            count: totalCount,
            next: response.next,
            previous: response.previous,
          });
        }
        // Case 2: Response is a single brand object with reports array
        else if (response.brand_id && response.reports && Array.isArray(response.reports)) {
          brandGroupsList = [response];
          totalCount = 1;
          setPagination({
            count: totalCount,
            next: null,
            previous: null,
          });
        }
        // Case 3: Response is an array of brand objects
        else if (Array.isArray(response)) {
          // Check if it's an array of brand objects
          if (response.length > 0 && response[0].brand_id && response[0].reports) {
            brandGroupsList = response.filter(
              (bg) => bg.reports && Array.isArray(bg.reports) && bg.reports.length > 0,
            );
            totalCount = brandGroupsList.length;
            setPagination({ count: totalCount, next: null, previous: null });
          } else {
            // Direct array of reports (backward compatibility - wrap in brand object)
            brandGroupsList = [
              {
                brand_id: 'default',
                brand_name: 'Reports',
                reports: response,
              },
            ];
            totalCount = 1;
            setPagination({ count: totalCount, next: null, previous: null });
          }
        }
        // Case 4: Response is an object with a single brand (no results wrapper)
        else if (response.brand_id && response.reports) {
          brandGroupsList = [response];
          totalCount = 1;
          setPagination({
            count: totalCount,
            next: null,
            previous: null,
          });
        }
      }

      // // Merge with localStorage reports (put localStorage first)
      // const localBrandGroups = loadLocalStorageReports();
      // if (localBrandGroups.length > 0) {
      //   // Combine: localStorage first, then API results
      //   const combined = [...localBrandGroups];
      //   brandGroupsList.forEach((bg) => {
      //     const exists = combined.find((b) => b.brand_id === bg.brand_id);
      //     if (exists) {
      //       // Merge reports, keeping localStorage ones first
      //       exists.reports = [...exists.reports, ...bg.reports];
      //     } else {
      //       combined.push(bg);
      //     }
      //   });
      //   setBrandGroups(combined);
      // } 
      setBrandGroups(brandGroupsList);
      setCurrentPage(page);
    } catch (err) {
      console.error('Error loading demo reports:', err);
      setError('Failed to load demo reports. Please try again.');
      setBrandGroups([]);
      setPagination({ count: 0, next: null, previous: null });
    } finally {
      setLoading(false);
    }
  };

  const handleNextPage = () => {
    if (pagination.next) {
      const nextPage = currentPage + 1;
      loadDemoReports(nextPage);
    }
  };

  const handlePreviousPage = () => {
    if (pagination.previous) {
      const prevPage = currentPage - 1;
      loadDemoReports(prevPage);
    }
  };

  const handleCreateReport = () => {
    const newPath = '/competitor-report-dashboard/create';
    if (isInIframe) {
      // Notify parent window to update URL
      window.parent.postMessage({
        type: 'COMPETITOR_REPORT_NAVIGATE',
        path: `/planner${newPath}`
      }, '*');
    }
    history.push(newPath);
  };

  const handleViewReport = (reportOrBrand) => {
    if (onViewReport) {
      onViewReport(reportOrBrand);
    } else {
      // Handle both report objects and brand objects
      if (reportOrBrand.brand_id) {
        const query = new URLSearchParams();
        query.set('platform', (reportOrBrand.platform || '').toLowerCase());
        if (reportOrBrand.start_date) query.set('start_date', reportOrBrand.start_date);
        if (reportOrBrand.end_date) query.set('end_date', reportOrBrand.end_date);

        // Prefer report_group_id (UUID) so Detail page uses the group_id lookup path.
        // Fall back to brand_id + ids for legacy records without a group UUID.
        let newPath;
        if (reportOrBrand.report_group_id) {
          newPath = `/competitor-report-dashboard/detail/${reportOrBrand.report_group_id}?${query.toString()}`;
        } else {
          const idsParam = Array.isArray(reportOrBrand.ids) ? reportOrBrand.ids.join(',') : reportOrBrand.ids;
          if (idsParam) query.set('ids', idsParam);
          newPath = `/competitor-report-dashboard/detail/${reportOrBrand.brand_id}?${query.toString()}`;
        }

        if (isInIframe) {
          // Notify parent window to update URL
          window.parent.postMessage({
            type: 'COMPETITOR_REPORT_NAVIGATE',
            path: `/planner${newPath}`
          }, '*');
        }
        history.push(newPath);
      }
    }
  };

  const handleEditReport = (payload) => {
    if (!payload?.report_group_id && !payload?.brand_id) return;
    const query = new URLSearchParams();
    if (payload.platform) query.set('platform', (payload.platform || '').toLowerCase());
    // Use report_group_id (UUID) so Create.jsx can look up the group correctly.
    const editIdentifier = payload.report_group_id || payload.brand_id;
    const newPath = `/competitor-report-dashboard/edit/${editIdentifier}?${query.toString()}`;
    if (isInIframe) {
      window.parent.postMessage({
        type: 'COMPETITOR_REPORT_NAVIGATE',
        path: `/planner${newPath}`,
      }, '*');
    }
    history.push(newPath);
  };

  const handleExportReport = (report) => {
    if (onExportReport) {
      onExportReport(report);
    } else {
      alert(`Exporting report: ${report.title}`);
    }
  };

  return (
      <Box className={classes.root}>
        {/* <Sidebar
        isCollapsed={isSidebarCollapsed}
        onToggle={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
      /> */}
        <Box
          className={`${classes.content}`}
          style={{
            flex: 1,
            transition: 'margin-left 0.3s ease',
            paddingLeft: 0,
          }}
        >
          <Container maxWidth="lg" className={classes.pageContent}>
            <Box className={classes.hero}>
              <Box className={classes.iconContainer}>
                <Assessment className={classes.icon} />
              </Box>
              <Typography variant="h1" className={classes.title}>
                Competitor Report
              </Typography>
              <Typography variant="body1" className={classes.subtitle}>
                View sample reports or create your own custom competitor analysis
              </Typography>
              <Button
                variant="contained"
                color="primary"
                size="medium"
                startIcon={<Add />}
                onClick={handleCreateReport}
                style={{ marginTop: 16 }}
              >
                Create New Report
              </Button>
            </Box>

            <Box className={classes.section}>
              <Typography variant="h2" className={classes.sectionTitle}>
                All Reports
              </Typography>
              <Typography variant="body2" className={classes.sectionDescription}>
                Explore pre-generated sample reports to see what insights you can gain
              </Typography>

              {loading ? (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                  <CircularProgress />
                </Box>
              ) : error ? (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                  <Typography color="error">{error}</Typography>
                </Box>
              ) : brandGroups.length === 0 ? (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                  <Typography color="textSecondary">No reports available</Typography>
                </Box>
              ) : (
                <>
                  <Grid container spacing={3}>
                    {brandGroups.map((brand) => (
                      <Grid
                        item
                        xs={12}
                        sm={6}
                        md={4}
                        key={brand.brand_id || `brand-${brand.brand_name}`}
                      >
                        <BrandCard
                          brand={brand}
                          onViewReport={handleViewReport}
                          onExportReport={handleExportReport}
                          onEditReport={handleEditReport}
                        />
                      </Grid>
                    ))}
                  </Grid>
                  {(pagination.next || pagination.previous || pagination.count > 0) && (
                    <Box className={classes.paginationContainer}>
                      <Box className={classes.paginationInfo}>
                        <Typography variant="body2" color="textSecondary">
                          Total: {pagination.count || brandGroups.length}{' '}
                          {pagination.count === 1 ? 'brand' : 'brands'}
                        </Typography>
                      </Box>
                      <Box className={classes.paginationInfo}>
                        <IconButton
                          onClick={handlePreviousPage}
                          disabled={!pagination.previous || loading}
                          size="small"
                        >
                          <ChevronLeft />
                        </IconButton>
                        <Typography variant="body2" style={{ margin: '0 16px' }}>
                          Page {currentPage}
                        </Typography>
                        <IconButton
                          onClick={handleNextPage}
                          disabled={!pagination.next || loading}
                          size="small"
                        >
                          <ChevronRight />
                        </IconButton>
                      </Box>
                    </Box>
                  )}
                </>
              )}
            </Box>
          </Container>
        </Box>
      </Box>
  );
}
